// Dependency-free entry point — no React, no `app/`. Consumed by the axios
// interceptor, `versionGuard`, and `app/appReset.ts`. React consumers import
// from `@/features/auth` instead.
import { sessionStore } from '../domain/session/sessionStore';
import type {
	AuthSession,
	CredentialEntry,
	RequestCredentialSnapshot,
	ScopeKey,
} from '../domain/session/types';

export type {
	AuthSession,
	SessionContextValue,
	CredentialEntry,
	RequestCredentialSnapshot,
	ScopeKey,
} from '../domain/session/types';
export { selectIsAuthenticated } from '../domain/session/selectors';
export { refreshAuthSession } from './refreshAuthSession';

export const getAuthSession = (): AuthSession => {
	const {
		accessToken,
		refreshToken,
		idToken,
		isSuperUser,
		isStaff,
		defaultCompanyId,
		delegationsIds,
	} = sessionStore.getState();
	return {
		accessToken,
		refreshToken,
		idToken,
		isSuperUser,
		isStaff,
		defaultCompanyId,
		delegationsIds,
	};
};

export const setAuthSession = (session: Partial<AuthSession>): void => {
	sessionStore.getState().set(session);
};

export const clearAuthSession = (): void => {
	sessionStore.getState().clear();
};

export const subscribeAuthSession = (listener: (session: AuthSession) => void): (() => void) =>
	sessionStore.subscribe(listener);

export const hydrateAuthSession = (): void => {
	sessionStore.getState().hydrate();
};

export const getActiveCredentialSnapshot = (): RequestCredentialSnapshot | null => {
	const { activeScopeKey, entries } = sessionStore.getState();
	if (!activeScopeKey) return null;
	const entry = entries[activeScopeKey];
	if (!entry) return null;
	return {
		scopeKey: activeScopeKey,
		entryRevision: entry.revision,
		accessToken: entry.accessToken,
	};
};

export const getEntry = (scopeKey: ScopeKey): CredentialEntry | null =>
	sessionStore.getState().entries[scopeKey] ?? null;

// A snapshot is admissible only while its scope is still the ACTIVE one and its
// entry still exists. Both halves matter: an evicted scope (rejected token,
// logout) has nothing to refresh, and a scope the session has since moved off
// holds a credential the backend revoked when it issued the current one — a late
// 401 from a request bound to it must reject, not trigger a refresh that fails
// and tears down the session the user is now in.
//
// The scope check used to be implicit: while the store held one entry at a time,
// "the entry exists" and "it is the active scope" were the same statement. Now
// that entries accumulate per visited scope, it has to be spelled out.
export const isSnapshotAdmissible = (
	snapshot: RequestCredentialSnapshot | null | undefined,
): boolean => {
	if (!snapshot) return false;
	const { activeScopeKey, entries } = sessionStore.getState();
	return snapshot.scopeKey === activeScopeKey && entries[snapshot.scopeKey] !== undefined;
};
